
import { action, observable, computed, autorun } from 'mobx';
import axios from 'axios';
import Cookies from 'js-cookie';

import { modalStore } from '~/common/stores/ui/modal.js';
import { formsStore } from '~/common/stores/ui/forms';
import { loginStore } from '~/common/stores/ui/loginStore';

import { ghRubyRep, ghRubyEmail, ghRubyRepEmail, ghRubyCode, ghRubyReactivation } from '~/common/globals';

import userInfo from '~/common/services/userInfo';
import userNumber from '~/common/services/userNumber';

import deepGet from '~/common/utils/deepGet.js';
import { isBoolean, isArray, isObject, isString } from '~/common/utils/typeCheckers';
import { RUBY_SIGNUP_PROGRAM, IS_PRODUCTION, RUBY_PROMO_CODES } from '~/common/utils/consts';
import logError from '~/common/utils/errorLogging';
import { gtm, purchaseEvent, completeBuyFlowEvent, errorPurchaseEvent } from '~/common/utils/tracking';

class RubyClass {
    @observable rubyPlans;
    @observable userConfirmedOption = false;
    @observable errorSubmittingRequest = false;
    @observable errorRequestIsAlreadySubmitted = false;
    @observable errorRequestUserIsNotAdmin = false;
    @observable errorAlreadyRubyUser = false;
    @observable errorAccountIsDunning = false;
    @observable errorAccountIsToBeCancelled = false;
    @observable errorAccountIsTrial = false;
    @observable rubyRep = '';
    @observable rubyEmail = '';
    @observable rubyRepEmail = '';
    @observable rubyCode = '';
    @observable isRubyReactivation = false;

    bootstrapStore() {
        // grab ruby rep's name
        autorun((reaction) => {
            // if there is a rep number query
            if (decodeURI(ghRubyRep())) {
                // save it to be passed to the api
                this.setRubyRep(ghRubyRep());
                reaction.dispose();
            }
        });

        // grab ruby rep's email
        autorun((reaction) => {
            // if there is a rep number query
            if (decodeURI(ghRubyRepEmail())) {
                // save it to be passed to the api
                this.setRubyRepEmail(ghRubyRepEmail());
                reaction.dispose();
            }
        });

        // grab ruby code
        autorun((reaction) => {
            // if there is a code in the url
            if (decodeURI(ghRubyCode())) {
                // save it to be passed to the api
                this.setRubyCode(ghRubyCode());
                reaction.dispose();
            }
        });

        // grab ruby reactivation value
        autorun((reaction) => {
            // if there is a reactivation query
            if (decodeURI(ghRubyReactivation()).toLowerCase() === 'true') {
                // save it to be passed to the api
                this.setRubyReactivation(true);
                reaction.dispose();
            }
        });

        // only work on dev enviroments
        if (!IS_PRODUCTION) {
            autorun((reaction) => {
                // if there is a rep number query
                if (decodeURI(ghRubyEmail())) {
                    // save it to be passed to the api
                    this.setRubyEmail(ghRubyEmail());
                    reaction.dispose();
                }
            });
        }
    }

    @action fetchRubyPlans() {
        axios.get('/api/receptionist/GetReceptionistplans').then((resp) => {
            const plans = resp.data;

            if (!plans || !Array.isArray(plans)) throw new Error('incorrect response for plans');
            // add default option with no value
            plans.unshift({ Name: 'Select a Plan', ChargeID: '' });
            this.setRubyPlans(plans);
        }).catch((err) => {
            this.setErrorSubmittingRequest(true);
            logError(err);
        });
    }

    // log in user to get auth token
    @action authUser() {
        const { Username, PasswordLogin } = formsStore;

        // clear any possible errors
        loginStore.setLoginError('');
        formsStore.clearFieldError('Username');
        formsStore.clearFieldError('PasswordLogin');

        loginStore.getVerificationCode({ userIdentification: Username, PasswordLogin });
    }

    @action validateUser(code) {
        loginStore.validateUser(code, (authToken) => {
            // get user info
            userInfo(authToken).then((resp) => {
                const { data } = resp;
                // if error from the API
                if (data.Success === false) throw new Error('failed user info API');
                // save user info
                loginStore.setUserInfo(data);

                const { VpsId, IsAdministrator, ReceptionistPlan, IsAccountInDunning } = loginStore.userInfo;

                userNumber(VpsId, loginStore.authToken).then((rsp) => {
                    loginStore.setUserNumbers(rsp.data);
                });
                // if user is not an admin
                if (!IsAdministrator) {
                    this.setErrorRequestUserIsNotAdmin(true);
                    // if GH account is on dunning
                } else if (IsAccountInDunning) {
                    this.setErrorAccountIsDunning(true);
                    // if user already has receptionist plan
                } else if (ReceptionistPlan) {
                    this.setErrorAlreadyRubyUser(true);
                }

                // close log in modal
                if (modalStore.childComponentString === 'loginModal' || modalStore.childComponentString === 'LoginLmiModal') modalStore.setModalOptions({ isModalOpen: false });
            }).catch((err) => {
                throw err;
            });
        }, false, false);
    }

    @action submitRubyRequest() {
        this.setErrorSubmittingRequest(false);
        this.setErrorRequestIsAlreadySubmitted(false);

        const options = { headers: { Authorization: loginStore.authToken } };
        const payload = {
            PlanName: this.planName,
            CustomerEmail: loginStore.userInfo.Email,
            InternalPlanName: this.planInternalName,
            RepresentativeName: this.rubyRep || '',
            RepresentativeEmail: this.rubyRepEmail || '',
            ChargeId: formsStore.RubyPlan,
            IsReactivation: this.isRubyReactivation
        };
        // add rubycode if present
        if (this.rubyCode) payload.PlanDiscount = this.rubyCode;

        axios.post('/api/receptionist/activate', payload, options).then((resp) => {
            const { Success } = resp.data;

            if (!Success || Success !== true) {
                gtm(errorPurchaseEvent(resp.data.ErrorText, 'add-on'));
                throw new Error('ruby request failed');
            }

            gtm(purchaseEvent(resp.data, 'add-on'));
            gtm(completeBuyFlowEvent(resp.data, 'add-on'));

            this.setUserConfirmedOption(true);
            this.alertMarketoOfRequest();
        }).catch((err) => {
            const ErrorText = deepGet(err, ['response', 'data', 'ErrorText']);
            // invalid or no error text so can't validate, just display generic error message
            if (!ErrorText || typeof ErrorText !== 'string') {
                gtm(errorPurchaseEvent('invalid', 'add-on'));
                this.setErrorSubmittingRequest(true);
            // if user is not an admin
            } else if (ErrorText.includes('Requesting user should be admin')) {
                gtm(errorPurchaseEvent('Requesting user should be admin', 'add-on'));
                this.setErrorRequestUserIsNotAdmin(true);
            // if the account is on dunning
            } else if (ErrorText.includes('User account is in dunning')) {
                gtm(errorPurchaseEvent('User account is in dunning', 'add-on'));
                this.setErrorAccountIsDunning(true);
            // if there is already a request open for ruby service
            } else if (ErrorText.includes('DuplicateUserNameExeception')) {
                gtm(errorPurchaseEvent('DuplicateUserNameExeception', 'add-on'));
                this.setErrorRequestIsAlreadySubmitted(true);
            // if there is already a ruby user
            } else if (ErrorText.includes('User already has active Receptionist feature')) {
                gtm(errorPurchaseEvent('User already has active Receptionist feature', 'add-on'));
                this.setErrorAlreadyRubyUser(true);
            // account is pending cancelation
            } else if (ErrorText.includes('User account is pending cancellation')) {
                gtm(errorPurchaseEvent('User account is pending cancellation', 'add-on'));
                this.setErrorAccountIsToBeCancelled(true);
            // account is trial
            } else if (ErrorText.includes('User account is in free trial')) {
                gtm(errorPurchaseEvent('User account is in free trial', 'add-on'));
                this.setErrorAccountIsTrial(true);
            // generic catch all error
            } else {
                gtm(errorPurchaseEvent('Error', 'add-on'));
                this.setErrorSubmittingRequest(true);
            }

            this.setUserConfirmedOption(false);
        });
    }

    @action alertMarketoOfRequest() {
        const payload = {
            email: loginStore.userInfo.Email,
            campaignID: RUBY_SIGNUP_PROGRAM,
            mkto_trk: Cookies.get('_mkto_trk') || ''
        };

        axios.post('/api/proxy/ruby-request', payload);
    }

    @action setRubyPlans(arr) {
        this.rubyPlans = isArray(arr, this.rubyPlans);
    }

    @action setRubyCode(str) {
        if (typeof str !== 'string' || str.length !== 4) return;
        const code = `${str.charAt(0)}${str.charAt(3)}`;
        const matchedCode = RUBY_PROMO_CODES.find((cd) => { return code === cd; });
        if (!matchedCode) return;
        this.rubyCode = parseFloat(`0.${matchedCode}`);
    }

    @action setUserConfirmedOption(bool) {
        this.userConfirmedOption = isBoolean(bool, this.userConfirmedOption);
    }

    @action setErrorSubmittingRequest(bool) {
        this.errorSubmittingRequest = isBoolean(bool, this.errorSubmittingRequest);
    }

    @action setErrorRequestIsAlreadySubmitted(bool) {
        this.errorRequestIsAlreadySubmitted = isBoolean(bool, this.errorRequestIsAlreadySubmitted);
    }

    @action setErrorRequestUserIsNotAdmin(bool) {
        this.errorRequestUserIsNotAdmin = isBoolean(bool, this.errorRequestUserIsNotAdmin);
    }

    @action setErrorAlreadyRubyUser(bool) {
        this.errorAlreadyRubyUser = isBoolean(bool, this.errorAlreadyRubyUser);
    }

    @action setErrorAccountIsDunning(bool) {
        this.errorAccountIsDunning = isBoolean(bool, this.errorAccountIsDunning);
    }

    @action setErrorAccountIsToBeCancelled(bool) {
        this.errorAccountIsToBeCancelled = isBoolean(bool, this.errorAccountIsToBeCancelled);
    }

    @action setErrorAccountIsTrial(bool) {
        this.errorAccountIsTrial = isBoolean(bool, this.errorAccountIsTrial);
    }

    @action setSelectedRubyPlan(obj) {
        this.selectedRubyPlan = isObject(obj, this.selectedRubyPlan);
    }

    @action setRubyRep(str) {
        this.rubyRep = isString(str, this.rubyRep);
    }

    @action setRubyRepEmail(str) {
        this.rubyRepEmail = isString(str, this.rubyRepEmail);
    }

    @action setRubyEmail(str) {
        // if it is a valid simple email this is for internal use only
        if (str && str.match(/^[A-z0-9-._{|}~-]+@[A-z0-9-_]+(\.[A-z]+)+$/)) {
            this.rubyEmail = isString(str, this.rubyEmail);
        }
    }

    @action setRubyReactivation(bool) {
        this.isRubyReactivation = isBoolean(bool, this.isRubyReactivation);
    }

    @computed get fetchedPlans() {
        return this.rubyPlans && this.rubyPlans.length > 0;
    }

    @computed get getPerMinuteCharges() {
        if (!this.rubyPlans) return false;

        const selectedPlan = this.rubyPlans.find((plan) => {
            return plan.ChargeID === formsStore.RubyPlan;
        });

        const features = JSON.parse(selectedPlan.CustomFeatureString);

        if (features.OveragePrice) return features.OveragePrice;

        return false;
    }

    @computed get planName() {
        if (!this.rubyPlans) return false;

        const selectedPlan = this.rubyPlans.find((plan) => {
            return plan.ChargeID === formsStore.RubyPlan;
        });

        return selectedPlan.Name || false;
    }

    @computed get planInternalName() {
        if (!this.rubyPlans) return false;

        const selectedPlan = this.rubyPlans.find((plan) => {
            return plan.ChargeID === formsStore.RubyPlan;
        });

        return selectedPlan.InternalName || false;
    }

    @computed get isErrorState() {
        return (this.errorSubmittingRequest || this.errorRequestIsAlreadySubmitted ||
            this.errorRequestUserIsNotAdmin || this.errorAccountIsDunning ||
            this.errorAccountIsToBeCancelled || this.errorAccountIsTrial
        );
    }

    @computed get canAddRuby() {
        return (!this.errorRequestIsAlreadySubmitted && !this.errorRequestUserIsNotAdmin &&
            !this.errorAlreadyRubyUser && !this.errorAccountIsDunning &&
            !this.errorAccountIsToBeCancelled && !this.errorAccountIsTrial);
    }

    @computed get selectedRubyPlan() {
        if (!this.rubyPlans) return false;

        const selectedPlan = this.rubyPlans.find((plan) => {
            return plan.ChargeID === formsStore.RubyPlan;
        });

        return selectedPlan || false;
    }
}

const rubyStore = new RubyClass();
export { rubyStore, RubyClass };
